import { GetStaticProps } from "next";
import { useTranslations } from "next-intl";
import Image from "next/image";
import Contact from "../components/Contact";
import Layout from "../components/Layout";
import { DiseasesQuery, Locale } from "../interfaces";
import sdk from "../lib/sdk";
import Pyramids from "../public/pyramids.jpg";
import Clinic2 from "../public/clinic2.jpg";
import Polar1000 from "../public/polar1000.jpg";
import { NextSeo } from "next-seo";

type Props = {
  diseases: DiseasesQuery["diseases"];
};

const Bioresonance = ({ diseases }: Props) => {
  const t = useTranslations("Bioresonance");

  return (
    <Layout>
      <NextSeo title={t("title")} description={t("description")} />
      <section className="container mx-auto mt-10">
        <div className="flex flex-wrap space-y-5 xl:flex-row-reverse">
          <div className="flex flex-row items-center justify-center flex-grow xl:w-1/2">
            <Image
              src={Pyramids}
              alt="Rayonex Pyramids"
              width={720}
              height={405}
              className="object-cover rounded-lg shadow-lg"
            />
          </div>
          <div className="flex flex-col justify-evenly xl:w-1/2">
            <h1 className="mb-3 text-4xl font-bold lg:text-5xl font-heading">
              {t("header")}
            </h1>
            <p>{t("p1")}</p>
            <p>{t("p2")}</p>
            <p>{t("p3")}</p>
            <p>{t("p4")}</p>
          </div>
        </div>
      </section>
      <section>
        <div className="py-20 bg-gray-50">
          <div className="container px-4 mx-auto">
            <div className="flex flex-wrap items-center justify-center">
              <div className="w-full mb-12 lg:w-1/2 lg:mb-0">
                <div className="lg:max-w-md lg:mx-auto">
                  <span className="font-bold text-sky-600">
                    {t("infoSection.pretitle")}
                  </span>
                  <h2 className="my-2 text-4xl font-bold lg:text-5xl font-heading">
                    {t("infoSection.title")}
                  </h2>
                  <p className="mb-6 leading-loose text-gray-500">
                    {t("infoSection.description")}
                  </p>
                  <ul className="font-bold text-gray-500">
                    <li className="flex mb-4">
                      <svg
                        className="w-6 h-6 mr-2 text-sky-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      <span>{t("infoSection.article1")}</span>
                    </li>
                    <li className="flex mb-4">
                      <svg
                        className="w-6 h-6 mr-2 text-sky-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      <span>{t("infoSection.article2")}</span>
                    </li>
                    <li className="flex mb-4">
                      <svg
                        className="w-6 h-6 mr-2 text-sky-400"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                          clipRule="evenodd"
                        ></path>
                      </svg>
                      <span>{t("infoSection.article3")}</span>
                    </li>
                  </ul>
                </div>
              </div>
              <div className="flex flex-wrap w-full -mx-4 lg:w-1/2">
                <div className="w-full px-4 mb-8 lg:mb-0 md:w-1/2">
                  <div className="py-6 pl-6 pr-4 mb-8 bg-white rounded shadow">
                    <span className="inline-block p-3 mb-4 bg-green-100 rounded-lg">
                      <svg
                        className="w-10 h-10 text-sky-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        ></path>
                      </svg>
                    </span>
                    <h4 className="mb-2 text-2xl font-bold font-heading">
                      {t("infoSection.box1title")}
                    </h4>
                    <p className="leading-loose text-gray-500">
                      {t("infoSection.box1")}
                    </p>
                  </div>
                  <div className="py-6 pl-6 pr-4 bg-white rounded shadow">
                    <span className="inline-block p-3 mb-4 bg-green-100 rounded-lg">
                      <svg
                        className="w-10 h-10 text-sky-600"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path d="M3 1a1 1 0 000 2h1.22l.305 1.222a.997.997 0 00.01.042l1.358 5.43-.893.892C3.74 11.846 4.632 14 6.414 14H15a1 1 0 000-2H6.414l1-1H14a1 1 0 00.894-.553l3-6A1 1 0 0017 3H6.28l-.31-1.243A1 1 0 005 1H3zM16 16.5a1.5 1.5 0 11-3 0 1.5 1.5 0 013 0zM6.5 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z"></path>
                      </svg>
                    </span>
                    <h4 className="mb-2 text-2xl font-bold font-heading">
                      {t("infoSection.box2title")}
                    </h4>
                    <p className="leading-loose text-gray-500">
                      {t("infoSection.box2")}
                    </p>
                  </div>
                </div>
                <div className="w-full px-4 md:w-1/2 lg:mt-20">
                  <div className="py-6 pl-6 pr-4 mb-8 bg-white rounded-lg shadow">
                    <span className="inline-block p-3 mb-4 bg-green-100 rounded">
                      <svg
                        className="w-10 h-10 text-sky-600"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M16 4v12l-4-2-4 2V4M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                        ></path>
                      </svg>
                    </span>
                    <h4 className="mb-2 text-2xl font-bold font-heading">
                      {t("infoSection.box3title")}
                    </h4>
                    <p className="leading-loose text-gray-500">
                      {t("infoSection.box3")}
                    </p>
                  </div>
                  <div className="py-6 pl-6 pr-4 bg-white rounded-lg shadow">
                    <span className="inline-block p-3 mb-4 bg-green-100 rounded">
                      <svg
                        className="w-10 h-10 text-sky-600"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M6 6V5a3 3 0 013-3h2a3 3 0 013 3v1h2a2 2 0 012 2v3.57A22.952 22.952 0 0110 13a22.95 22.95 0 01-8-1.43V8a2 2 0 012-2h2zm2-1a1 1 0 011-1h2a1 1 0 011 1v1H8V5zm1 5a1 1 0 011-1h.01a1 1 0 110 2H10a1 1 0 01-1-1z"
                          clipRule="evenodd"
                        ></path>
                        <path d="M2 13.692V16a2 2 0 002 2h12a2 2 0 002-2v-2.308A24.974 24.974 0 0110 15c-2.796 0-5.487-.46-8-1.308z"></path>
                      </svg>
                    </span>
                    <h4 className="mb-2 text-2xl font-bold font-heading">
                      {t("infoSection.box4title")}
                    </h4>
                    <p className="leading-loose text-gray-500">
                      {t("infoSection.box4")}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container px-4 mx-auto">
          <div className="flex flex-wrap items-center justify-center">
            <div className="w-auto px-10 py-8 mb-10 bg-white rounded lg:mb-0 lg:mr-8">
              <Image
                className="h-16"
                src="/sole_logo.png"
                alt="Dr. Birsel Uçkun Logo without text"
                width={100}
                height={166}
              />
            </div>
            <div className="w-full mb-10 text-center lg:w-auto lg:mb-0 lg:text-left">
              <h2 className="max-w-3xl mx-auto mb-2 text-4xl font-bold lg:mx-0 lg:text-5xl font-heading">
                {t("bookTitle")}
              </h2>
              <p className="max-w-xl mx-auto leading-loose text-gray-500 lg:mx-0">
                {t("bookSubtitle")}
              </p>
            </div>
            <div className="flex justify-center flex-grow text-center lg:w-auto lg:ml-auto">
              <a
                className="inline-block px-6 py-2 font-bold leading-loose text-white transition duration-200 bg-primary hover:bg-sky-700 rounded-l-xl rounded-t-xl"
                href="#contact"
              >
                {t("bookButton")}
              </a>
            </div>
          </div>
        </div>
      </section>
      <section>
        <div className="container mx-auto mt-10">
          <div className="flex flex-wrap space-y-5">
            <div className="flex flex-row items-center justify-center flex-grow xl:w-1/2">
              <div>
                <Image
                  src={Clinic2}
                  alt="Dr. Birsel Uçkun Bioresonance Clinic"
                  width={720}
                  height={405}
                  className="object-cover rounded-lg shadow-lg"
                />
              </div>
            </div>
            <div className="flex flex-col justify-evenly xl:w-1/2">
              <h2 className="mb-3 text-2xl font-bold lg:text-3xl font-heading">
                {t("benefitTitle")}
              </h2>
              <p>{t("benefit")}</p>
            </div>
          </div>
        </div>
        <div className="container mx-auto mt-10">
          <div className="flex flex-wrap md:flex-row-reverse">
            <div className="flex flex-row items-center justify-center flex-grow xl:w-1/2">
              <Image
                src={Polar1000}
                alt="Rayonex Polar 1000"
                width={720}
                height={405}
              />
            </div>
            <div className="flex flex-col justify-evenly xl:w-1/2">
              <h2 className="mb-3 text-2xl font-bold lg:text-3xl font-heading">
                {t("causeTitle")}
              </h2>
              <p>{t("cause1")}</p>
              <p>{t("cause2")}</p>
              <p>{t("cause3")}</p>
            </div>
          </div>
        </div>
      </section>
      <section className="py-20">
        <div className="container px-4 mx-auto text-center">
          <h3 className="mb-12 text-2xl font-heading">{t("disease")}</h3>
          <div className="grid grid-cols-8 gap-4 auto-rows-fr">
            {diseases.map((disease) => (
              <div
                className="flex items-center justify-center py-16 rounded bg-gray-50"
                key={disease.name}
              >
                {disease.name}
              </div>
            ))}
          </div>
        </div>
      </section>
      <Contact />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async ({ locale }) => {
  const { diseases } = await sdk.Diseases({ locale: locale as Locale });

  return {
    props: {
      diseases,
      messages: (await import(`../messages/${locale}.json`)).default,
    },
  };
};

export default Bioresonance;
